import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import CrmPipelinesAPI from 'dashboard/api/crmPipelines';

const CONVERSATIONS_PAGE_SIZE = 25;
const CONVERSATIONS_MAX_PAGES = 200; // safety cap (~5k conversations)

// KREACHAT CUSTOM: template stage names are seed data copied into the user's own
// editable pipeline on creation, so (unlike static UI chrome) they stay as literals
// here rather than i18n keys; only the template picker's own name/description are
// translated, since those are the actual static UI the picker renders.
export function getCrmTemplates(t) {
  return [
    {
      id: 'empty',
      name: t('CRM.TEMPLATES.EMPTY_FUNNEL'),
      description: t('CRM.TEMPLATES.EMPTY_FUNNEL_DESC'),
      icon: 'i-lucide-layout-grid',
      stages: [],
    },
    {
      id: 'sales',
      name: t('CRM.TEMPLATES.SALES_PIPELINE'),
      description: t('CRM.TEMPLATES.SALES_PIPELINE_DESC'),
      icon: 'i-lucide-dollar-sign',
      stages: [
        {
          id: 'novo_contato',
          name: 'Novo Contato',
          color: '#3B82F6',
          label: 'novo_contato',
        },
        {
          id: 'atendimento',
          name: 'Atendimento',
          color: '#EAB308',
          label: 'atendimento',
        },
        {
          id: 'orcamento',
          name: 'Orçamento',
          color: '#A855F7',
          label: 'orcamento',
        },
        {
          id: 'follow_up',
          name: 'Follow Up',
          color: '#6366F1',
          label: 'follow_up',
        },
        { id: 'comprou', name: 'Comprou', color: '#22C55E', label: 'comprou' },
        {
          id: 'perdido',
          name: 'Perdido',
          color: '#EF4444',
          label: 'perdido',
          action: 'resolve',
        },
      ],
    },
    {
      id: 'support',
      name: t('CRM.TEMPLATES.SUPPORT'),
      description: t('CRM.TEMPLATES.SUPPORT_DESC'),
      icon: 'i-lucide-headphones',
      stages: [
        {
          id: 'novo_ticket',
          name: 'Novo Ticket',
          color: '#3B82F6',
          label: 'novo_ticket',
        },
        {
          id: 'em_analise',
          name: 'Em Análise',
          color: '#F59E0B',
          label: 'em_analise',
        },
        {
          id: 'em_andamento',
          name: 'Em Andamento',
          color: '#6366F1',
          label: 'em_andamento',
        },
        {
          id: 'aguardando_cliente',
          name: 'Aguardando Cliente',
          color: '#EC4899',
          label: 'aguardando_cliente',
        },
        {
          id: 'resolvido',
          name: 'Resolvido',
          color: '#22C55E',
          label: 'resolvido',
          action: 'resolve',
        },
        {
          id: 'fechado',
          name: 'Fechado',
          color: '#64748B',
          label: 'fechado',
          action: 'resolve',
        },
      ],
    },
    {
      id: 'recruitment',
      name: t('CRM.TEMPLATES.RECRUITMENT'),
      description: t('CRM.TEMPLATES.RECRUITMENT_DESC'),
      icon: 'i-lucide-users',
      stages: [
        {
          id: 'candidatura',
          name: 'Candidatura',
          color: '#3B82F6',
          label: 'candidatura',
        },
        { id: 'triagem', name: 'Triagem', color: '#8B5CF6', label: 'triagem' },
        {
          id: 'entrevista_agendada',
          name: 'Entrevista Agendada',
          color: '#F59E0B',
          label: 'entrevista_agendada',
        },
        {
          id: 'em_avaliacao',
          name: 'Em Avaliação',
          color: '#06B6D4',
          label: 'em_avaliacao',
        },
        {
          id: 'proposta_enviada',
          name: 'Proposta Enviada',
          color: '#10B981',
          label: 'proposta_enviada',
        },
        {
          id: 'contratado',
          name: 'Contratado',
          color: '#22C55E',
          label: 'contratado',
        },
      ],
    },
  ];
}

const currentPipelineId = ref('');
const currentView = ref('kanban'); // 'kanban' | 'list'
const searchQuery = ref('');
const selectedAgentId = ref('all');
const selectedInboxId = ref('all');
const slaOnlyFilter = ref(false);
const isSettingsModalOpen = ref(false);
const isCreateModalOpen = ref(false);
const pipelines = ref([]);

export function useCrm() {
  const store = useStore();
  const { accountId } = useAccount();
  const { t } = useI18n();

  // KREACHAT CUSTOM: pipelines/stages are persisted on the backend (Crm::Pipeline /
  // Crm::Stage), not localStorage — see B7 in the migration audit.
  async function loadPipelines() {
    try {
      const { data } = await CrmPipelinesAPI.get();
      pipelines.value = data.payload || [];
    } catch (e) {
      pipelines.value = [];
    }

    if (
      pipelines.value.length > 0 &&
      !pipelines.value.some(
        p => String(p.id) === String(currentPipelineId.value)
      )
    ) {
      currentPipelineId.value = pipelines.value[0].id;
    }
  }

  async function createPipeline({ name, description, templateId }) {
    const templates = getCrmTemplates(t);
    const template =
      templates.find(tpl => tpl.id === templateId) || templates[0];
    const stagesAttributes = template.stages.map((s, idx) => ({
      name: s.name,
      color: s.color,
      label: s.label,
      action: s.action,
      position: idx,
    }));

    const { data: newPipeline } = await CrmPipelinesAPI.create({
      pipeline: {
        name: name || template.name,
        description: description || template.description || '',
        stages_attributes: stagesAttributes,
      },
    });

    pipelines.value = [...pipelines.value, newPipeline];
    currentPipelineId.value = newPipeline.id;
    return newPipeline;
  }

  // KREACHAT CUSTOM: CrmFunnelSettingsView emits the whole desired stage list
  // (additions/removals/reorders already applied client-side). Diff it against
  // the pipeline's current stages so Rails' nested attributes can create new
  // stages, update existing ones (matched by id) and destroy removed ones.
  async function updatePipeline(pipelineId, data) {
    const current = pipelines.value.find(
      p => String(p.id) === String(pipelineId)
    );
    const existingIds = new Set((current?.stages || []).map(s => s.id));
    const submittedIds = new Set(
      (data.stages || []).filter(s => existingIds.has(s.id)).map(s => s.id)
    );
    const destroyedStages = (current?.stages || [])
      .filter(s => !submittedIds.has(s.id))
      .map(s => ({ id: s.id, _destroy: true }));

    const stagesAttributes = [
      ...(data.stages || []).map((s, idx) => {
        const attrs = {
          name: s.name,
          color: s.color,
          label: s.label,
          action: s.action,
          position: idx,
        };
        if (existingIds.has(s.id)) attrs.id = s.id;
        return attrs;
      }),
      ...destroyedStages,
    ];

    const { data: updatedPipeline } = await CrmPipelinesAPI.update(pipelineId, {
      pipeline: {
        name: data.name,
        description: data.description,
        stages_attributes: stagesAttributes,
      },
    });
    pipelines.value = pipelines.value.map(p =>
      String(p.id) === String(pipelineId) ? updatedPipeline : p
    );
  }

  async function deletePipeline(pipelineId) {
    await CrmPipelinesAPI.delete(pipelineId);
    const updated = pipelines.value.filter(
      p => String(p.id) !== String(pipelineId)
    );
    pipelines.value = updated;
    if (String(currentPipelineId.value) === String(pipelineId)) {
      currentPipelineId.value = updated[0]?.id || '';
    }
  }

  // KREACHAT CUSTOM: fetch every conversation page instead of only the first 25 (I2)
  async function fetchAllConversationsPaginated() {
    for (let page = 1; page <= CONVERSATIONS_MAX_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const result = await store.dispatch('fetchAllConversations', {
        status: 'all',
        assignee_type: 'all',
        page,
      });
      const fetchedCount = result?.payload?.length || 0;
      if (fetchedCount < CONVERSATIONS_PAGE_SIZE) break;
    }
  }

  // KREACHAT CUSTOM: only load pipelines here — useCrm() is also called from
  // Sidebar.vue (mounted on every dashboard page) and CrmStageSelector.vue
  // (mounted in every open conversation's contact panel). The paginated
  // conversation fetch below is expensive on accounts with real volume (it can
  // take dozens of sequential requests), so it must stay opt-in and only run
  // where it's actually needed — the CRM board itself (see Index.vue).
  onMounted(() => {
    loadPipelines();
  });

  watch(accountId, () => {
    loadPipelines();
  });

  // KREACHAT CUSTOM: called explicitly by the CRM board (Index.vue) on mount
  // and account switch — not from this composable's own onMounted/watch above.
  function loadBoardData() {
    fetchAllConversationsPaginated();
    store.dispatch('labels/get');
    store.dispatch('inboxes/get');
    store.dispatch('agents/get');
    store.dispatch('teams/get');
  }

  // KREACHAT CUSTOM (B4 fix): fall back to null instead of a never-declared
  // `defaultPipelines`, which crashed the CRM screen and sidebar on any account
  // with zero pipelines (new account, or before the first pipeline is created).
  const activePipeline = computed(() => {
    return (
      pipelines.value.find(
        p => String(p.id) === String(currentPipelineId.value)
      ) ||
      pipelines.value[0] ||
      null
    );
  });

  const allConversations = useMapGetter('getAllConversations');
  const allAgents = useMapGetter('agents/getAgents');
  const allInboxes = useMapGetter('inboxes/getInboxes');
  const allTeams = useMapGetter('teams/getTeams');

  const filteredConversations = computed(() => {
    let list = allConversations.value || [];

    // Filter out conversations removed/excluded from kanban
    list = list.filter(c => {
      const customAttrs = c.custom_attributes || {};
      return !customAttrs.exclude_kanban && !customAttrs.kanban_hidden;
    });

    if (selectedInboxId.value !== 'all') {
      const inboxIdNum = Number(selectedInboxId.value);
      list = list.filter(c => c.inbox_id === inboxIdNum);
    }

    if (selectedAgentId.value !== 'all') {
      if (selectedAgentId.value === 'unassigned') {
        list = list.filter(c => !c.meta || !c.meta.assignee);
      } else {
        const agentIdNum = Number(selectedAgentId.value);
        list = list.filter(
          c => c.meta && c.meta.assignee && c.meta.assignee.id === agentIdNum
        );
      }
    }

    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase().trim();
      list = list.filter(c => {
        const contactName = c.meta?.sender?.name?.toLowerCase() || '';
        const contactPhone = c.meta?.sender?.phone_number?.toLowerCase() || '';
        const contactEmail = c.meta?.sender?.email?.toLowerCase() || '';
        const teamName = c.meta?.team?.name?.toLowerCase() || '';
        const idStr = String(c.id);
        const displayIdStr = String(c.display_id || '');
        return (
          contactName.includes(query) ||
          contactPhone.includes(query) ||
          contactEmail.includes(query) ||
          teamName.includes(query) ||
          idStr.includes(query) ||
          displayIdStr.includes(query)
        );
      });
    }

    if (slaOnlyFilter.value) {
      list = list.filter(c => {
        const sla = getSlaInfo(c);
        return sla.slaLevel === 'critical' || sla.slaLevel === 'warning';
      });
    }

    return list;
  });

  const conversationsByStage = computed(() => {
    const stages = activePipeline.value?.stages || [];
    const stageMap = {};
    stages.forEach(stage => {
      stageMap[stage.id] = [];
    });

    filteredConversations.value.forEach(conversation => {
      const labels = conversation.labels || [];
      const isResolved = conversation.status === 'resolved';

      if (isResolved) {
        const resolveStage = stages.find(s => s.action === 'resolve');
        if (resolveStage) {
          stageMap[resolveStage.id].push(conversation);
          return;
        }
      }

      const matchedStage = stages.find(
        s =>
          (s.label && labels.includes(s.label)) ||
          (s.id !== undefined &&
            (labels.includes(s.id) ||
              labels.includes(String(s.id)) ||
              labels.includes(`stage_${s.id}`)))
      );

      if (matchedStage) {
        stageMap[matchedStage.id].push(conversation);
      } else if (stages.length > 0) {
        if (conversation.meta && conversation.meta.assignee && stages[1]) {
          stageMap[stages[1].id].push(conversation);
        } else {
          stageMap[stages[0].id].push(conversation);
        }
      }
    });

    return stageMap;
  });

  // Calculate sum of deal values (R$) per column
  const columnTotals = computed(() => {
    const totals = {};
    const stages = activePipeline.value?.stages || [];
    stages.forEach(stage => {
      const convs = conversationsByStage.value[stage.id] || [];
      totals[stage.id] = convs.reduce((sum, c) => sum + getDealValue(c), 0);
    });
    return totals;
  });

  function getDealValue(conversation) {
    if (!conversation) return 0;
    const customAttrs = conversation.custom_attributes || {};
    const val = customAttrs.deal_value ?? customAttrs.value ?? 0;
    return parseFloat(val) || 0;
  }

  function formatCurrency(amount) {
    const numeric = parseFloat(amount) || 0;
    return numeric.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  async function updateDealValue(conversationId, newValue) {
    const conversationIdNum = Number(conversationId);
    const conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    if (!conversation) return;

    const numericValue = parseFloat(newValue) || 0;
    const updatedCustomAttrs = {
      ...(conversation.custom_attributes || {}),
      deal_value: numericValue,
    };

    conversation.custom_attributes = updatedCustomAttrs;
    store.dispatch('updateConversation', {
      ...conversation,
      custom_attributes: updatedCustomAttrs,
    });

    try {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationIdNum,
        customAttributes: updatedCustomAttrs,
      });
    } catch (e) {
      console.error('Failed to update deal value:', e);
    }
  }

  function getDealStatus(conversation) {
    if (!conversation) return 'open';
    const customAttrs = conversation.custom_attributes || {};

    // Check stage from labels in activePipeline
    const stages = activePipeline.value?.stages || [];
    const labels = conversation.labels || [];
    const currentStage = stages.find(
      s =>
        (s.label && labels.includes(s.label)) ||
        (s.id !== undefined &&
          (labels.includes(s.id) ||
            labels.includes(String(s.id)) ||
            labels.includes(`stage_${s.id}`)))
    );

    if (currentStage) {
      const stageNameLower = (currentStage.name || '').toLowerCase();
      if (
        currentStage.action === 'lost' ||
        stageNameLower.includes('perdid') ||
        stageNameLower.includes('lost') ||
        stageNameLower.includes('cancelad')
      ) {
        return 'lost';
      }
      if (
        currentStage.action === 'won' ||
        currentStage.action === 'resolve' ||
        stageNameLower.includes('ganh') ||
        stageNameLower.includes('won') ||
        stageNameLower.includes('comprou') ||
        stageNameLower.includes('fechad')
      ) {
        return 'won';
      }
    }

    if (customAttrs.deal_status) return customAttrs.deal_status;
    if (conversation.status === 'resolved') return 'won';
    return 'open';
  }

  async function updateDealStatus(conversationId, status) {
    const conversationIdNum = Number(conversationId);
    const conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    if (!conversation) return;

    const updatedCustomAttrs = {
      ...(conversation.custom_attributes || {}),
      deal_status: status,
    };

    conversation.custom_attributes = updatedCustomAttrs;

    let targetSystemStatus = conversation.status;
    if (status === 'won' || status === 'lost') {
      targetSystemStatus = 'resolved';
    } else if (status === 'open') {
      targetSystemStatus = 'open';
    }

    conversation.status = targetSystemStatus;

    store.dispatch('updateConversation', {
      ...conversation,
      status: targetSystemStatus,
      custom_attributes: updatedCustomAttrs,
    });

    try {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationIdNum,
        customAttributes: updatedCustomAttrs,
      });

      await store.dispatch('toggleStatus', {
        conversationId: conversationIdNum,
        status: targetSystemStatus,
      });
    } catch (e) {
      console.error('Failed to update deal status:', e);
    }
  }

  async function renameLead(conversationId, newName) {
    const conversationIdNum = Number(conversationId);
    const conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    if (!conversation || !newName.trim()) return;

    const cleanName = newName.trim();
    if (conversation.meta?.sender) {
      conversation.meta.sender.name = cleanName;
    }

    store.dispatch('updateConversation', { ...conversation });

    const contactId = conversation.meta?.sender?.id;
    if (contactId) {
      try {
        await store.dispatch('contacts/update', {
          id: contactId,
          name: cleanName,
        });
      } catch (e) {
        console.error('Failed to rename contact:', e);
      }
    }
  }

  function getSlaInfo(conversation) {
    const timestamp =
      conversation.waiting_since ||
      conversation.last_activity_at ||
      conversation.created_at;

    if (!timestamp) {
      return {
        elapsedMinutes: 0,
        formattedTime: '0m',
        slaLevel: 'normal',
      };
    }

    const elapsedMs = Date.now() - timestamp * 1000;
    const elapsedMinutes = Math.max(0, Math.floor(elapsedMs / (1000 * 60)));

    let formattedTime = `${elapsedMinutes}m`;
    if (elapsedMinutes >= 60 && elapsedMinutes < 1440) {
      const hours = Math.floor(elapsedMinutes / 60);
      formattedTime = `${hours}h`;
    } else if (elapsedMinutes >= 1440) {
      const days = Math.floor(elapsedMinutes / 1440);
      formattedTime = `${days}d`;
    }

    // Regra de classificação de SLA:
    // <= 30 minutos: normal (cinza)
    // > 30 minutos e <= 60 minutos: warning (laranja)
    // > 60 minutos (após 1 hora): critical (vermelho suave com pulso)
    let slaLevel = 'normal';
    if (elapsedMinutes > 60) {
      slaLevel = 'critical';
    } else if (elapsedMinutes > 30) {
      slaLevel = 'warning';
    }

    return { elapsedMinutes, formattedTime, slaLevel };
  }

  function formatDateTime(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} | ${hours}:${minutes}:${seconds}`;
  }

  function getChannelInfo(conversation) {
    const channelType =
      conversation.channel || conversation.meta?.channel || '';
    let name = 'WhatsApp';
    let icon = 'i-lucide-message-circle';
    let brandClass = 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30';
    let brandHex = '#25D366';

    if (channelType.includes('Whatsapp') || channelType.includes('whatsapp')) {
      name = 'WhatsApp';
      icon = 'i-lucide-message-circle';
      brandClass = 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30';
      brandHex = '#25D366';
    } else if (
      channelType.includes('Instagram') ||
      channelType.includes('instagram')
    ) {
      name = 'Instagram';
      icon = 'i-lucide-instagram';
      brandClass = 'text-pink-500 bg-pink-500/10 border-pink-500/30';
      brandHex = '#E1306C';
    } else if (
      channelType.includes('Facebook') ||
      channelType.includes('facebook')
    ) {
      name = 'Facebook';
      icon = 'i-lucide-facebook';
      brandClass = 'text-blue-600 bg-blue-600/10 border-blue-600/30';
      brandHex = '#1877F2';
    } else if (channelType.includes('Email') || channelType.includes('email')) {
      name = 'Email';
      icon = 'i-lucide-mail';
      brandClass = 'text-amber-500 bg-amber-500/10 border-amber-500/30';
      brandHex = '#F59E0B';
    } else if (channelType.includes('Web') || channelType.includes('widget')) {
      name = 'Live Chat';
      icon = 'i-lucide-globe';
      brandClass = 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30';
      brandHex = '#00A8FF';
    } else if (channelType.includes('Api') || channelType.includes('api')) {
      name = 'API / Bot';
      icon = 'i-lucide-bot';
      brandClass = 'text-purple-500 bg-purple-500/10 border-purple-500/30';
      brandHex = '#A855F7';
    }

    return { name, icon, brandClass, brandHex };
  }

  function getSourceInfo(conversation) {
    const customAttrs = conversation.custom_attributes || {};
    let utmSource =
      customAttrs.utm_source ||
      customAttrs.channel_origin ||
      customAttrs.ads_source ||
      null;

    if (!utmSource) {
      if (customAttrs.gclid) utmSource = 'Google Ads';
      else if (customAttrs.fbclid) utmSource = 'Meta Ads';
    }

    const srcLower = (utmSource || '').toLowerCase();
    let name = utmSource || t('CRM.NO_SOURCE');
    let icon = 'i-lucide-help-circle';
    let colorClass =
      'text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';

    if (
      srcLower.includes('google ads') ||
      srcLower.includes('gads') ||
      srcLower.includes('googleads')
    ) {
      name = 'Google Ads';
      icon = 'i-logos-google-ads';
      colorClass =
        'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800';
    } else if (
      srcLower.includes('meta') ||
      srcLower.includes('facebook') ||
      srcLower.includes('fb')
    ) {
      name = 'Meta Ads';
      icon = 'i-logos-meta-icon';
      colorClass =
        'text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800';
    } else if (srcLower.includes('instagram') || srcLower.includes('ig')) {
      name = 'Instagram';
      icon = 'i-logos-instagram-icon';
      colorClass =
        'text-pink-600 bg-pink-50 border-pink-200 dark:bg-pink-950/40 dark:text-pink-400 dark:border-pink-800';
    } else if (
      srcLower.includes('business') ||
      srcLower.includes('gmb') ||
      srcLower.includes('meu negócio') ||
      srcLower.includes('meu negocio')
    ) {
      name = 'Google Meu Negócio';
      icon = 'i-logos-google-maps';
      colorClass =
        'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800';
    } else if (srcLower.includes('tiktok')) {
      name = 'TikTok Ads';
      icon = 'i-logos-tiktok-icon';
      colorClass =
        'text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-800';
    } else if (
      srcLower.includes('organico') ||
      srcLower.includes('orgânico') ||
      srcLower.includes('seo')
    ) {
      name = 'Orgânico';
      icon = 'i-lucide-leaf';
      colorClass =
        'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800';
    } else if (srcLower.includes('direto')) {
      name = 'Direto';
      icon = 'i-lucide-arrow-right-circle';
      colorClass =
        'text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    } else if (
      srcLower.includes('indicação') ||
      srcLower.includes('indicacao')
    ) {
      name = 'Indicação';
      icon = 'i-lucide-users';
      colorClass =
        'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800';
    } else if (srcLower.includes('outras') || srcLower.includes('outros')) {
      name = 'Outras Origens';
      icon = 'i-lucide-compass';
      colorClass =
        'text-indigo-600 bg-indigo-50 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-400 dark:border-indigo-800';
    }

    return { name, icon, colorClass };
  }

  function getTeamInfo(conversation) {
    if (!conversation) return null;
    return conversation.meta?.team?.name || null;
  }

  async function moveConversationToStage(conversationId, targetStageId) {
    const conversationIdNum = Number(conversationId);
    let conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    if (!conversation) {
      conversation = store.getters.getConversationById?.(conversationIdNum);
    }
    if (!conversation) return;

    const stages = activePipeline.value?.stages || [];
    const targetStage = stages.find(
      s =>
        String(s.id) === String(targetStageId) ||
        String(s.label) === String(targetStageId)
    );
    if (!targetStage) return;

    const currentLabels = [...(conversation.labels || [])];
    const stageLabels = stages
      .flatMap(s => [s.label, s.id, String(s.id)])
      .filter(Boolean);

    const updatedLabels = currentLabels.filter(
      lbl => !stageLabels.some(sl => String(sl) === String(lbl))
    );
    const newStageLabel = targetStage.label || String(targetStage.id);
    if (newStageLabel && !updatedLabels.includes(newStageLabel)) {
      updatedLabels.push(newStageLabel);
    }

    const stageNameLower = (targetStage.name || '').toLowerCase();
    let newDealStatus = 'open';
    let targetSystemStatus = 'open';

    if (
      targetStage.action === 'lost' ||
      stageNameLower.includes('perdid') ||
      stageNameLower.includes('lost') ||
      stageNameLower.includes('cancelad')
    ) {
      newDealStatus = 'lost';
      targetSystemStatus = 'resolved';
    } else if (
      targetStage.action === 'won' ||
      targetStage.action === 'resolve' ||
      stageNameLower.includes('ganh') ||
      stageNameLower.includes('won') ||
      stageNameLower.includes('comprou') ||
      stageNameLower.includes('fechad')
    ) {
      newDealStatus = 'won';
      targetSystemStatus = 'resolved';
    } else {
      newDealStatus = 'open';
      targetSystemStatus = 'open';
    }

    const updatedCustomAttrs = {
      ...(conversation.custom_attributes || {}),
      deal_status: newDealStatus,
      exclude_kanban: false,
      kanban_hidden: false,
    };

    const updatedConversation = {
      ...conversation,
      labels: updatedLabels,
      custom_attributes: updatedCustomAttrs,
      status: targetSystemStatus,
    };

    store.dispatch('updateConversation', updatedConversation);

    try {
      await store.dispatch('conversationLabels/update', {
        conversationId: conversationIdNum,
        labels: updatedLabels,
      });

      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationIdNum,
        customAttributes: updatedCustomAttrs,
      });

      if (conversation.status !== targetSystemStatus) {
        await store.dispatch('toggleStatus', {
          conversationId: conversationIdNum,
          status: targetSystemStatus,
        });
      }
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  }

  async function assignToMe(conversationId) {
    const currentUser = store.getters.getCurrentUser;
    if (!currentUser) return;
    const conversationIdNum = Number(conversationId);
    const conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    const agentObj = {
      id: currentUser.id,
      name: currentUser.name || currentUser.available_name || 'Agente',
      thumbnail: currentUser.avatar_url || currentUser.thumbnail || '',
      avatar_url: currentUser.avatar_url || currentUser.thumbnail || '',
    };
    if (conversation) {
      conversation.meta = conversation.meta || {};
      conversation.meta.assignee = agentObj;
      store.dispatch('updateConversation', { ...conversation });
    }
    try {
      await store.dispatch('assignAgent', {
        conversationId: conversationIdNum,
        assignee: agentObj,
      });
    } catch (e) {
      console.error('Failed to assign agent to me:', e);
    }
  }

  async function resolveConversation(conversationId) {
    try {
      await store.dispatch('toggleStatus', {
        conversationId,
        status: 'resolved',
      });
    } catch (e) {}
  }

  async function deleteCardFromKanban(conversationId) {
    const conversationIdNum = Number(conversationId);
    const conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    if (!conversation) return;

    // Remove from Kanban by marking custom attribute exclude_kanban: true
    const updatedCustomAttrs = {
      ...(conversation.custom_attributes || {}),
      exclude_kanban: true,
    };

    // Remove any stage labels
    const currentLabels = [...(conversation.labels || [])];
    const stageLabels = (activePipeline.value?.stages || [])
      .map(s => s.label || s.id)
      .filter(Boolean);
    const updatedLabels = currentLabels.filter(
      lbl => !stageLabels.includes(lbl)
    );

    conversation.custom_attributes = updatedCustomAttrs;
    conversation.labels = updatedLabels;

    store.dispatch('updateConversation', {
      ...conversation,
      custom_attributes: updatedCustomAttrs,
      labels: updatedLabels,
    });

    try {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationIdNum,
        customAttributes: updatedCustomAttrs,
      });
      await store.dispatch('conversationLabels/update', {
        conversationId: conversationIdNum,
        labels: updatedLabels,
      });
    } catch (e) {
      console.error('Failed to remove card from kanban:', e);
    }
  }

  async function duplicateCard(conversation) {
    if (!conversation) return;

    const contactId = conversation.meta?.sender?.id;
    const inboxId = conversation.inbox_id;
    const customAttrs = {
      ...(conversation.custom_attributes || {}),
      deal_value: getDealValue(conversation),
    };

    try {
      const newConv = await store.dispatch('contactConversations/create', {
        params: {
          inboxId,
          sourceId: conversation.contact_inbox?.source_id,
          message: {
            content: t('CRM.DUPLICATE_TOAST'),
          },
          assigneeId: conversation.meta?.assignee?.id,
        },
        contactId,
      });

      if (newConv && newConv.id) {
        if (conversation.labels && conversation.labels.length > 0) {
          await store.dispatch('conversationLabels/update', {
            conversationId: newConv.id,
            labels: [...conversation.labels],
          });
        }
        await store.dispatch('updateCustomAttributes', {
          conversationId: newConv.id,
          customAttributes: customAttrs,
        });
      }
    } catch (e) {
      console.error('Failed to duplicate card:', e);
    }
  }

  async function updateCardFull(updateData) {
    const {
      conversationId,
      contactId,
      name,
      avatarFile,
      stageId,
      agentId,
      inboxId,
      dealValue: newDealVal,
      dealStatus: newDealStatus,
      customAttributes: newCustomAttrs,
    } = updateData;

    const conversationIdNum = Number(conversationId);
    const conversation = allConversations.value.find(
      c => c.id === conversationIdNum
    );
    if (!conversation) return;

    // 1. Update Contact Name & Avatar
    if (contactId && (name || avatarFile)) {
      try {
        const contactPayload = {
          id: contactId,
          ...(name ? { name } : {}),
          ...(avatarFile ? { avatar: avatarFile, isFormData: true } : {}),
        };

        if (avatarFile && conversation.meta?.sender) {
          const previewUrl = URL.createObjectURL(avatarFile);
          conversation.meta.sender.thumbnail = previewUrl;
          conversation.meta.sender.avatar_url = previewUrl;
        }

        const updatedContact = await store.dispatch('contacts/update', contactPayload);
        if (conversation.meta?.sender) {
          if (name) conversation.meta.sender.name = name;
          if (updatedContact?.thumbnail || updatedContact?.avatar_url) {
            conversation.meta.sender.thumbnail =
              updatedContact.thumbnail || updatedContact.avatar_url;
            conversation.meta.sender.avatar_url =
              updatedContact.avatar_url || updatedContact.thumbnail;
          }
        }

        store.dispatch('updateConversationContact', {
          conversationId: conversationIdNum,
          ...(conversation.meta?.sender || {}),
          ...(updatedContact || {}),
        });
      } catch (e) {
        console.error('Failed to update contact in full card update:', e);
      }
    }

    // 2. Update Deal Value, Status and Custom Attributes (UTMs / Source)
    const currentStatus = conversation.status;
    let targetSystemStatus = currentStatus;
    if (newDealStatus === 'won' || newDealStatus === 'lost') {
      targetSystemStatus = 'resolved';
    } else if (newDealStatus === 'open') {
      targetSystemStatus = 'open';
    }

    conversation.custom_attributes = {
      ...(conversation.custom_attributes || {}),
      ...newCustomAttrs,
      deal_value: newDealVal,
      deal_status: newDealStatus,
    };
    conversation.status = targetSystemStatus;

    store.dispatch('updateConversation', { ...conversation });

    try {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationIdNum,
        customAttributes: conversation.custom_attributes,
      });

      if (targetSystemStatus !== currentStatus) {
        await store.dispatch('toggleStatus', {
          conversationId: conversationIdNum,
          status: targetSystemStatus,
        });
      }
    } catch (e) {
      console.error('Failed to update custom attributes in card edit:', e);
    }

    // 3. Move stage if specified
    if (stageId) {
      await moveConversationToStage(conversationIdNum, stageId);
    }

    // 4. Assign agent if specified
    if (agentId !== undefined) {
      try {
        if (agentId) {
          const targetAgent = (allAgents.value || []).find(
            a => String(a.id) === String(agentId)
          );
          const agentObj = targetAgent
            ? {
                ...targetAgent,
                thumbnail: targetAgent.thumbnail || targetAgent.avatar_url || '',
                avatar_url: targetAgent.avatar_url || targetAgent.thumbnail || '',
              }
            : null;

          if (conversation.meta) {
            conversation.meta.assignee = agentObj;
          }
          await store.dispatch('assignAgent', {
            conversationId: conversationIdNum,
            assignee: agentObj,
          });
        } else {
          if (conversation.meta) {
            conversation.meta.assignee = null;
          }
          await store.dispatch('assignAgent', {
            conversationId: conversationIdNum,
            assignee: null,
          });
        }
        store.dispatch('updateConversation', { ...conversation });
      } catch (e) {
        console.error('Failed to assign agent in card update:', e);
      }
    }
  }

  return {
    pipelines,
    currentPipelineId,
    currentView,
    activePipeline,
    searchQuery,
    selectedAgentId,
    selectedInboxId,
    slaOnlyFilter,
    isSettingsModalOpen,
    isCreateModalOpen,
    createPipeline,
    updatePipeline,
    deletePipeline,
    loadBoardData,
    allAgents,
    allInboxes,
    allTeams,
    conversationsByStage,
    filteredConversations,
    columnTotals,
    getDealValue,
    formatCurrency,
    updateDealValue,
    getDealStatus,
    updateDealStatus,
    renameLead,
    getSlaInfo,
    formatDateTime,
    getChannelInfo,
    getSourceInfo,
    getTeamInfo,
    moveConversationToStage,
    assignToMe,
    resolveConversation,
    deleteCardFromKanban,
    duplicateCard,
    updateCardFull,
  };
}
